import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from '@/components/ui/sheet.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { MoneyInput } from '@/components/ui/money-input.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import {
  AlertTriangle, CheckCircle2, ExternalLink, Plus, Search,
} from 'lucide-react';
import { ApiError } from '@/lib/api.js';
import {
  listHolders, createHolder, issueCertificate, sendCertificate, previewCoiPdf,
  getDealPrefill,
} from '@/services/coi.service.js';
import { getDeal } from '@/services/deals.service.js';
import { cn } from '@/lib/utils.js';

const FORM_TYPES = [
  { key: 'acord_28', label: 'ACORD 28', desc: 'Evidence of Commercial Property' },
];

const inputClass = cn(
  'flex min-h-[36px] w-full rounded-card border border-border bg-bg px-3 py-1.5 text-sm text-text',
  'placeholder:text-text-dim',
  'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-1 focus:ring-offset-bg',
  'disabled:cursor-not-allowed disabled:opacity-50'
);

function defaultPolicy() {
  return {
    insurer_name: '', naic_code: '', policy_number: '',
    effective_date: '', expiration_date: '',
    policy_type_description: '', form_type: 'special',
    property_limit: '', property_deductible: '',
    building: true, bpp: false,
    replacement_cost: 'yes', agreed_value: 'no',
    coinsurance: 'na', coinsurance_pct: '',
    subrogation_waiver: 'no', blanket: 'no',
    terrorism: 'na', terrorism_exclusion: 'na', domestic_terrorism_exclusion: 'na',
    limited_fungus: { covered: 'na', limit: '', deductible: '' },
    wind_hail: { covered: 'na', limit: '', deductible: '', subject_to_provisions: 'na' },
    named_windstorm: { covered: 'na', limit: '', deductible: '', subject_to_provisions: 'na' },
    ordinance_undamaged: { covered: 'na', limit: '', deductible: '' },
    ordinance_demolition: { covered: 'na', limit: '', deductible: '' },
    ordinance_icc: { covered: 'na', limit: '', deductible: '' },
    earth_movement: { covered: 'na' },
    flood: { covered: 'na' },
    equipment_breakdown: { covered: 'na' },
    business_income: { covered: 'na', limit: '' },
  };
}

function defaultLocation() {
  return { description: '', street1: '', city: '', state: '', zip: '' };
}

// Coerce a prefill value (number or "$1,234"-style string) to a raw number for
// MoneyInput. Returns undefined when empty/unparseable so `?? prev` falls back.
function toNum(v) {
  if (v == null || v === '') return undefined;
  const n = Number(String(v).replace(/[,$\s]/g, ''));
  return Number.isFinite(n) ? n : undefined;
}

export default function COISheet({ dealId, accountId, open, onOpenChange, onChange }) {
  const [step, setStep] = useState(1);
  const [holders, setHolders] = useState([]);
  const [selectedHolder, setSelectedHolder] = useState(null);
  const [holderSearch, setHolderSearch] = useState('');
  const [showNewHolder, setShowNewHolder] = useState(false);
  const [newHolder, setNewHolder] = useState({ name: '', street1: '', city: '', state: '', zip: '', email: '', attention: '' });
  const [formType, setFormType] = useState('acord_28');
  const [policy, setPolicy] = useState(defaultPolicy);
  const [location, setLocation] = useState(defaultLocation);
  const [certSettings, setCertSettings] = useState({
    additional_insured: false, waiver_of_subrogation: false,
    primary_and_noncontributory: false, description_of_operations: '',
    holder_mortgagee: false, holder_loss_payee: false,
    holder_lenders_loss_payable: false, holder_contract_of_sale: false,
  });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const previewUrlRef = useRef(null);
  const [issuing, setIssuing] = useState(false);
  const [sending, setSending] = useState(false);
  const [formError, setFormError] = useState(null);
  const [done, setDone] = useState(null);
  const [sendTo, setSendTo] = useState('');
  const [sendCc, setSendCc] = useState('');
  // 198 §2: THE PRIMARY CONTACT IS ALWAYS AN AVAILABLE RECIPIENT.
  //   David: "if i don't have an email address for the cert holder, it should
  //   ask if it should instead go to the primary. being sent to the primary
  //   should always be an option, regardless."
  // Named, not a blank the broker has to know to fill. The To box was never
  // prefilled at all, which is what sent him hunting for an edit link.
  const [primary, setPrimary] = useState(null);

  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setStep(1);
        setSelectedHolder(null);
        setHolderSearch('');
        setShowNewHolder(false);
        setNewHolder({ name: '', street1: '', city: '', state: '', zip: '', email: '', attention: '' });
        setFormType('acord_28');
        setPolicy(defaultPolicy());
        setLocation(defaultLocation());
        setCertSettings({ additional_insured: false, waiver_of_subrogation: false, primary_and_noncontributory: false, description_of_operations: '' });
        setDone(null);
        setFormError(null);
        setPreviewUrl(null);
        setSendTo('');
        setSendCc('');
        setPrimary(null);
        if (previewUrlRef.current) { URL.revokeObjectURL(previewUrlRef.current); previewUrlRef.current = null; }
      }, 250);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  // The deal's primary contact, for the recipient row below. A failed read
  // leaves `primary` null and the box simply stays empty and typeable, which is
  // exactly today's behaviour: this can add an option, never remove one.
  useEffect(() => {
    if (!open || !dealId) return;
    let live = true;
    getDeal(dealId)
      .then((res) => {
        const p = (res?.deal || res)?.primary_contact;
        if (live && p && p.email) setPrimary({ name: p.name || 'the primary contact', email: p.email });
      })
      .catch(() => { /* non-blocking: the field stays typeable */ });
    return () => { live = false; };
  }, [open, dealId]);

  // Prefill the To box with the primary as soon as we know it, without ever
  // overwriting an address the broker has already typed.
  useEffect(() => {
    if (primary?.email) setSendTo((cur) => cur || primary.email);
  }, [primary]);

  useEffect(() => {
    if (!open || !accountId) return;
    listHolders(accountId)
      .then((res) => setHolders(res?.holders || []))
      .catch(() => setHolders([]));
  }, [open, accountId]);

  // Pre-fill policy/coverage from the Deal when the sheet opens.
  useEffect(() => {
    if (!open || !dealId) return;
    getDealPrefill(dealId)
      .then((res) => {
        const pf = res?.prefill;
        if (!pf) return;
        setPolicy((prev) => ({
          ...prev,
          insurer_name: pf.insurer_name || prev.insurer_name,
          naic_code: pf.naic_code || prev.naic_code,
          policy_number: pf.policy_number || prev.policy_number,
          effective_date: pf.effective_date || prev.effective_date,
          expiration_date: pf.expiration_date || prev.expiration_date,
          policy_type_description: pf.policy_type_description || prev.policy_type_description,
          property_limit: toNum(pf.coverage?.property_limit) ?? prev.property_limit,
          property_deductible: toNum(pf.coverage?.property_deductible) ?? prev.property_deductible,
          coinsurance: pf.coverage?.coinsurance || prev.coinsurance,
          coinsurance_pct: pf.coverage?.coinsurance_pct || prev.coinsurance_pct,
          flood: pf.coverage?.flood || prev.flood,
          earth_movement: pf.coverage?.earth_movement || prev.earth_movement,
          wind_hail: pf.coverage?.wind_hail || prev.wind_hail,
        }));
        if (pf.location?.description || pf.location?.street1) {
          setLocation((prev) => ({
            ...prev,
            description: pf.location.description || prev.description,
            street1: pf.location.street1 || prev.street1,
            city: pf.location.city || prev.city,
            state: pf.location.state || prev.state,
            zip: pf.location.zip || prev.zip,
          }));
        }
      })
      .catch(() => {});
  }, [open, dealId]);

  useEffect(() => () => {
    if (previewUrlRef.current) { URL.revokeObjectURL(previewUrlRef.current); previewUrlRef.current = null; }
  }, []);

  const filteredHolders = useMemo(() => {
    if (!holderSearch.trim()) return holders;
    const q = holderSearch.toLowerCase();
    return holders.filter((h) => h.name.toLowerCase().includes(q));
  }, [holders, holderSearch]);

  async function handleCreateHolder() {
    const h = newHolder;
    if (!h.name || !h.street1 || !h.city || !h.state || !h.zip) {
      setFormError('Name, street, city, state, and zip are required.');
      return;
    }
    setFormError(null);
    try {
      const res = await createHolder({ ...h, crm_account_id: accountId });
      if (!res?.holder) throw new Error('create failed');
      setHolders((prev) => [...prev, res.holder]);
      selectHolder(res.holder);
      setShowNewHolder(false);
      toast.success(`Holder "${res.holder.name}" added`);
    } catch (e) {
      setFormError(e.message);
    }
  }

  function selectHolder(h) {
    setSelectedHolder(h);
    setCertSettings((prev) => ({
      ...prev,
      additional_insured: h.default_additional_insured || false,
      waiver_of_subrogation: h.default_waiver_of_subrogation || false,
      primary_and_noncontributory: h.default_primary_and_noncontributory || false,
      description_of_operations: h.default_description_of_operations || '',
    }));
    setSendCc(h.email || '');
    setStep(2);
  }

  async function handlePreview() {
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const blob = await previewCoiPdf({
        deal_id: dealId,
        holder_id: selectedHolder?.id,
        form_type: formType,
        overrides: {
          ...certSettings,
          coverage_limits: policy,
          location,
          insurer_name: policy.insurer_name,
          naic_code: policy.naic_code,
          policy_number: policy.policy_number,
          effective_date: policy.effective_date,
          expiration_date: policy.expiration_date,
          policy_type_description: policy.policy_type_description,
        },
      });
      const url = URL.createObjectURL(blob);
      const prev = previewUrlRef.current;
      previewUrlRef.current = url;
      setPreviewUrl(url);
      if (prev) URL.revokeObjectURL(prev);
      setStep(3);
    } catch (e) {
      setPreviewError(e.message);
    } finally {
      setPreviewLoading(false);
    }
  }

  async function handleIssue() {
    setIssuing(true);
    setFormError(null);
    try {
      const res = await issueCertificate({
        deal_id: dealId,
        holder_id: selectedHolder.id,
        form_type: formType,
        ...certSettings,
        overrides: {
          coverage_limits: policy,
          location,
          insurer_name: policy.insurer_name,
          naic_code: policy.naic_code,
          policy_number: policy.policy_number,
          effective_date: policy.effective_date,
          expiration_date: policy.expiration_date,
          policy_type_description: policy.policy_type_description,
        },
      });
      if (!res?.success) throw new Error(res?.error || 'Issue failed');
      setDone(res);
      setStep(4);
      toast.success(`Issued ${formType.replace('acord_', 'ACORD ')} to ${selectedHolder.name}`);
      if (typeof onChange === 'function') onChange();
    } catch (e) {
      const msg = e instanceof ApiError ? e.body?.error : e.message;
      setFormError(msg || 'Issue failed');
    } finally {
      setIssuing(false);
    }
  }

  async function handleSend() {
    if (!sendTo) { setFormError('Recipient email required'); return; }
    setSending(true);
    setFormError(null);
    try {
      const res = await sendCertificate(done.certificate.id, { to_email: sendTo, cc_email: sendCc });
      if (!res?.success) throw new Error('Send failed');
      toast.success(`Cert sent to ${sendTo}`);
      if (typeof onChange === 'function') onChange();
    } catch (e) {
      setFormError(e.message);
    } finally {
      setSending(false);
    }
  }

  const formLabel = formType?.replace('acord_', 'ACORD ') || '';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className={cn('flex max-h-[96vh] flex-col overflow-hidden md:mx-auto', step === 3 ? 'md:max-w-[1400px]' : 'md:max-w-6xl')}
      >
        <SheetHeader>
          <SheetTitle>Issue Certificate of Insurance</SheetTitle>
          <SheetDescription>
            {selectedHolder ? `${formLabel} → ${selectedHolder.name}` : 'Select a certificate holder to begin'}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-1 py-4">
          <div className={cn('grid gap-4', step === 4 ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1', step !== 3 && step !== 4 && 'max-w-2xl')}>
            {step !== 3 ? (
            <div className="space-y-3">
              {step === 1 ? (
                <HolderPicker
                  holders={filteredHolders} search={holderSearch}
                  onSearchChange={setHolderSearch} onSelect={selectHolder}
                  showNew={showNewHolder} onShowNew={() => setShowNewHolder(true)}
                  newHolder={newHolder}
                  onNewHolderChange={(k, v) => setNewHolder((p) => ({ ...p, [k]: v }))}
                  onCreateHolder={handleCreateHolder}
                />
              ) : null}

              {step === 2 ? (
                <>
                  <FieldSet legend="Form type">
                    <div className="flex gap-2">
                      {FORM_TYPES.map((ft) => (
                        <label key={ft.key} className={cn(
                          'flex-1 cursor-pointer rounded-card border p-2 text-center transition-colors',
                          formType === ft.key ? 'border-accent bg-accent-soft' : 'border-border hover:bg-bg/40',
                        )}>
                          <input type="radio" name="coi-ft" value={ft.key} checked={formType === ft.key}
                            onChange={() => setFormType(ft.key)} className="sr-only" />
                          <div className="text-sm font-medium text-text">{ft.label}</div>
                          <div className="text-[10px] text-text-muted">{ft.desc}</div>
                        </label>
                      ))}
                    </div>
                  </FieldSet>

                  <FieldSet legend="Insurer / Carrier">
                    <div className="grid grid-cols-3 gap-2">
                      <Field label="Carrier name" className="col-span-2">
                        <Input value={policy.insurer_name} onChange={(e) => setPolicy(p => ({ ...p, insurer_name: e.target.value }))} placeholder="e.g. Imperium Insurance Company" />
                      </Field>
                      <Field label="NAIC">
                        <Input value={policy.naic_code} onChange={(e) => setPolicy(p => ({ ...p, naic_code: e.target.value }))} placeholder="e.g. 35408" />
                      </Field>
                    </div>
                  </FieldSet>

                  <FieldSet legend="Policy">
                    <div className="grid grid-cols-3 gap-2">
                      <Field label="Policy number" className="col-span-1">
                        <Input value={policy.policy_number} onChange={(e) => setPolicy(p => ({ ...p, policy_number: e.target.value }))} placeholder="BR-2026-001" />
                      </Field>
                      <Field label="Effective">
                        <Input type="date" value={policy.effective_date} onChange={(e) => setPolicy(p => ({ ...p, effective_date: e.target.value }))} />
                      </Field>
                      <Field label="Expiration">
                        <Input type="date" value={policy.expiration_date} onChange={(e) => setPolicy(p => ({ ...p, expiration_date: e.target.value }))} />
                      </Field>
                    </div>
                    <Field label="Policy type description">
                      <Input value={policy.policy_type_description} onChange={(e) => setPolicy(p => ({ ...p, policy_type_description: e.target.value }))} placeholder="e.g. Builders Risk" />
                    </Field>
                  </FieldSet>

                  {formType === 'acord_28' ? (
                    <>
                      <FieldSet legend="Perils insured">
                        <div className="flex gap-2">
                          {['basic', 'broad', 'special'].map((pk) => (
                            <label key={pk} className={cn(
                              'flex-1 cursor-pointer rounded-card border p-1.5 text-center text-xs capitalize transition-colors',
                              policy.form_type === pk ? 'border-accent bg-accent-soft text-text' : 'border-border text-text-muted hover:bg-bg/40',
                            )}>
                              <input type="radio" name="perils" value={pk} checked={policy.form_type === pk}
                                onChange={() => setPolicy(p => ({ ...p, form_type: pk }))} className="sr-only" />
                              {pk}
                            </label>
                          ))}
                        </div>
                      </FieldSet>

                      <FieldSet legend="Coverage">
                        <div className="grid grid-cols-2 gap-2">
                          <Field label="Property limit ($)">
                            <MoneyInput value={policy.property_limit} onValueChange={({ floatValue }) => setPolicy(p => ({ ...p, property_limit: floatValue ?? '' }))} placeholder="500,000" />
                          </Field>
                          <Field label="Deductible ($)">
                            <MoneyInput value={policy.property_deductible} onValueChange={({ floatValue }) => setPolicy(p => ({ ...p, property_deductible: floatValue ?? '' }))} placeholder="5,000" />
                          </Field>
                        </div>
                        <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
                          <CertToggle label="Building" checked={policy.building} onChange={(v) => setPolicy(p => ({ ...p, building: v }))} />
                          <CertToggle label="Business Personal Property" checked={policy.bpp} onChange={(v) => setPolicy(p => ({ ...p, bpp: v }))} />
                        </div>
                        <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5">
                          <Tristate label="Replacement Cost" value={policy.replacement_cost} onChange={(v) => setPolicy(p => ({ ...p, replacement_cost: v }))} />
                          <Tristate label="Agreed Value" value={policy.agreed_value} onChange={(v) => setPolicy(p => ({ ...p, agreed_value: v }))} />
                          <div className="flex items-center gap-1">
                            <Tristate label="Coinsurance" value={policy.coinsurance} onChange={(v) => setPolicy(p => ({ ...p, coinsurance: v }))} />
                            <Input value={policy.coinsurance_pct} onChange={(e) => setPolicy(p => ({ ...p, coinsurance_pct: e.target.value }))} placeholder="%" className="w-14 h-7 px-1.5 text-[11px]" />
                          </div>
                          <Tristate label="Waiver of Subrogation" value={policy.subrogation_waiver} onChange={(v) => setPolicy(p => ({ ...p, subrogation_waiver: v }))} />
                          <Tristate label="Blanket" value={policy.blanket} onChange={(v) => setPolicy(p => ({ ...p, blanket: v }))} />
                        </div>
                      </FieldSet>

                      <FieldSet legend="Terrorism">
                        <div className="grid grid-cols-1 gap-y-1.5">
                          <Tristate label="Terrorism Coverage" value={policy.terrorism} onChange={(v) => setPolicy(p => ({ ...p, terrorism: v }))} />
                          <Tristate label="Terrorism Excluded" value={policy.terrorism_exclusion} onChange={(v) => setPolicy(p => ({ ...p, terrorism_exclusion: v }))} />
                          <Tristate label="Domestic Terrorism Excluded" value={policy.domestic_terrorism_exclusion} onChange={(v) => setPolicy(p => ({ ...p, domestic_terrorism_exclusion: v }))} />
                        </div>
                      </FieldSet>

                      <FieldSet legend="Catastrophe / special perils">
                        <CoverageLine label="Wind / Hail" line={policy.wind_hail} onChange={(l) => setPolicy(p => ({ ...p, wind_hail: l }))} showProvisions />
                        <CoverageLine label="Named Storm" line={policy.named_windstorm} onChange={(l) => setPolicy(p => ({ ...p, named_windstorm: l }))} showProvisions />
                        <CoverageLine label="Earth Movement" line={policy.earth_movement} onChange={(l) => setPolicy(p => ({ ...p, earth_movement: l }))} noAmounts />
                        <CoverageLine label="Flood" line={policy.flood} onChange={(l) => setPolicy(p => ({ ...p, flood: l }))} noAmounts />
                        <CoverageLine label="Limited Fungus" line={policy.limited_fungus} onChange={(l) => setPolicy(p => ({ ...p, limited_fungus: l }))} />
                        <CoverageLine label="Equipment Breakdown" line={policy.equipment_breakdown} onChange={(l) => setPolicy(p => ({ ...p, equipment_breakdown: l }))} noAmounts />
                        <CoverageLine label="Business Income / Rental" line={policy.business_income} onChange={(l) => setPolicy(p => ({ ...p, business_income: l }))} noDeductible />
                      </FieldSet>

                      <FieldSet legend="Ordinance or Law">
                        <CoverageLine label="Undamaged portion of building" line={policy.ordinance_undamaged} onChange={(l) => setPolicy(p => ({ ...p, ordinance_undamaged: l }))} noDeductible />
                        <CoverageLine label="Demolition costs" line={policy.ordinance_demolition} onChange={(l) => setPolicy(p => ({ ...p, ordinance_demolition: l }))} noDeductible />
                        <CoverageLine label="Incr. cost of construction" line={policy.ordinance_icc} onChange={(l) => setPolicy(p => ({ ...p, ordinance_icc: l }))} noDeductible />
                      </FieldSet>

                      <FieldSet legend="Location">
                        <Field label="Description">
                          <Input value={location.description} onChange={(e) => setLocation(l => ({ ...l, description: e.target.value }))} placeholder="e.g. New hangar construction at KAHN" />
                        </Field>
                        <Field label="Address">
                          <Input value={location.street1} onChange={(e) => setLocation(l => ({ ...l, street1: e.target.value }))} placeholder="Street" />
                        </Field>
                        <div className="mt-1 grid grid-cols-3 gap-2">
                          <Input value={location.city} onChange={(e) => setLocation(l => ({ ...l, city: e.target.value }))} placeholder="City" />
                          <Input value={location.state} onChange={(e) => setLocation(l => ({ ...l, state: e.target.value }))} placeholder="ST" maxLength={2} className="uppercase" />
                          <Input value={location.zip} onChange={(e) => setLocation(l => ({ ...l, zip: e.target.value }))} placeholder="ZIP" />
                        </div>
                      </FieldSet>
                    </>
                  ) : null}

                  <FieldSet legend="Certificate holder settings">
                    <CertToggle label="Additional Insured" checked={certSettings.additional_insured} onChange={(v) => setCertSettings(s => ({ ...s, additional_insured: v }))} />
                    <CertToggle label="Waiver of Subrogation" checked={certSettings.waiver_of_subrogation} onChange={(v) => setCertSettings(s => ({ ...s, waiver_of_subrogation: v }))} />
                    <CertToggle label="Primary & Non-Contributory" checked={certSettings.primary_and_noncontributory} onChange={(v) => setCertSettings(s => ({ ...s, primary_and_noncontributory: v }))} />
                    <div className="mt-2 border-t border-border pt-2">
                      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-text-dim">Holder interest (optional)</div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                        <CertToggle label="Mortgagee" checked={certSettings.holder_mortgagee} onChange={(v) => setCertSettings(s => ({ ...s, holder_mortgagee: v }))} />
                        <CertToggle label="Loss Payee" checked={certSettings.holder_loss_payee} onChange={(v) => setCertSettings(s => ({ ...s, holder_loss_payee: v }))} />
                        <CertToggle label="Lender's Loss Payable" checked={certSettings.holder_lenders_loss_payable} onChange={(v) => setCertSettings(s => ({ ...s, holder_lenders_loss_payable: v }))} />
                        <CertToggle label="Contract of Sale" checked={certSettings.holder_contract_of_sale} onChange={(v) => setCertSettings(s => ({ ...s, holder_contract_of_sale: v }))} />
                      </div>
                    </div>
                  </FieldSet>
                </>
              ) : null}

              {step === 4 && done ? (
                <SuccessBody done={done} formLabel={formLabel} holderName={selectedHolder?.name} />
              ) : null}
            </div>
            ) : null}

            {step >= 3 ? (
              <div className="lg:sticky lg:top-0 lg:self-start">
                <PreviewPane url={previewUrl} loading={previewLoading} error={previewError} formLabel={formLabel} />
              </div>
            ) : null}
          </div>

          {formError ? (
            <div className="mt-3 flex items-start gap-2 rounded-card border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
              <AlertTriangle size={14} strokeWidth={1.75} className="mt-0.5 shrink-0" />
              <span>{formError}</span>
            </div>
          ) : null}
        </div>

        {step < 4 ? (
          <SheetFooter className="border-t border-border bg-surface px-4 py-3">
            <Button variant="ghost" onClick={() => onOpenChange?.(false)}>Cancel</Button>
            {step === 2 ? (
              <Button onClick={handlePreview} disabled={previewLoading} className="bg-accent text-white hover:bg-accent/90">
                {previewLoading ? 'Rendering...' : 'Preview'}
              </Button>
            ) : null}
            {step === 3 ? (
              <>
                <Button variant="outline" onClick={() => { setStep(2); setPreviewUrl(null); }}>Edit</Button>
                <Button onClick={handleIssue} disabled={issuing} className="bg-accent text-white hover:bg-accent/90">
                  {issuing ? 'Issuing...' : 'Issue certificate'}
                </Button>
              </>
            ) : null}
          </SheetFooter>
        ) : (
          <SheetFooter className="flex-col items-stretch gap-2 border-t border-border bg-surface px-4 py-3">
            {/* 198 §2.1: the recipients are NAMED, and the primary is offered
                whether or not the holder has an address. A blank box the broker
                has to know to fill is not an option, it is a quiz. */}
            <div className="flex flex-wrap items-center gap-2 text-[11px] text-text-muted">
              {primary ? (
                <button
                  type="button"
                  onClick={() => setSendTo(primary.email)}
                  className="rounded-card border border-border px-2 py-1 hover:border-accent hover:text-accent"
                  title={primary.email}
                >
                  Primary: {primary.name}
                </button>
              ) : (
                <span className="text-text-dim">No primary contact email on this deal</span>
              )}
              {selectedHolder ? (
                selectedHolder.email ? (
                  <button
                    type="button"
                    onClick={() => setSendTo(selectedHolder.email)}
                    className="rounded-card border border-border px-2 py-1 hover:border-accent hover:text-accent"
                    title={selectedHolder.email}
                  >
                    Holder: {selectedHolder.name}
                  </button>
                ) : (
                  // Said out loud rather than left as an empty CC box, which is
                  // what made a holder with no address look like a dead end.
                  <span className="text-text-dim">No email for {selectedHolder.name}; send to the primary, or type one.</span>
                )
              ) : null}
            </div>
            <div className="flex flex-1 items-center gap-2">
              <Input value={sendTo} onChange={(e) => setSendTo(e.target.value)} placeholder="To (insured email)" className="flex-1" />
              <Input value={sendCc} onChange={(e) => setSendCc(e.target.value)} placeholder="CC (holder email)" className="flex-1" />
              <Button onClick={handleSend} disabled={sending} className="bg-accent text-white hover:bg-accent/90">
                {sending ? 'Sending...' : 'Send'}
              </Button>
              <Button variant="ghost" onClick={() => onOpenChange?.(false)}>Close</Button>
            </div>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ── Sub-components (module scope per Rule #16) ──────────────────────

function HolderPicker({ holders, search, onSearchChange, onSelect, showNew, onShowNew, newHolder, onNewHolderChange, onCreateHolder }) {
  return (
    <FieldSet legend="Certificate holder">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-3 text-text-dim" />
        <Input value={search} onChange={(e) => onSearchChange(e.target.value)} placeholder="Search holders..." className="pl-8" />
      </div>
      <div className="mt-2 max-h-48 space-y-1 overflow-y-auto">
        {holders.map((h) => (
          <button key={h.id} type="button" onClick={() => onSelect(h)}
            className="flex w-full items-center justify-between rounded-card border border-border px-3 py-2 text-left text-sm hover:bg-bg/40 transition-colors">
            <div>
              <div className="font-medium text-text">{h.name}</div>
              <div className="text-[11px] text-text-muted">{h.city}, {h.state} {h.zip}</div>
            </div>
          </button>
        ))}
        {holders.length === 0 ? <p className="px-3 py-2 text-xs italic text-text-dim">No holders on file.</p> : null}
      </div>
      {!showNew ? (
        <button type="button" onClick={onShowNew} className="mt-2 flex items-center gap-1 text-xs text-accent hover:underline">
          <Plus size={12} /> Add new holder
        </button>
      ) : (
        <NewHolderForm holder={newHolder} onChange={onNewHolderChange} onCreate={onCreateHolder} />
      )}
    </FieldSet>
  );
}

function NewHolderForm({ holder, onChange, onCreate }) {
  return (
    <div className="mt-2 space-y-2 rounded-card border border-border bg-bg p-3">
      <div className="text-xs font-semibold uppercase tracking-wider text-text-muted">New holder</div>
      <Input value={holder.name} onChange={(e) => onChange('name', e.target.value)} placeholder="Name (e.g. LH Construction LLC)" />
      <Input value={holder.attention} onChange={(e) => onChange('attention', e.target.value)} placeholder="Attention (optional)" />
      <Input value={holder.street1} onChange={(e) => onChange('street1', e.target.value)} placeholder="Street address" />
      <div className="grid grid-cols-3 gap-2">
        <Input value={holder.city} onChange={(e) => onChange('city', e.target.value)} placeholder="City" />
        <Input value={holder.state} onChange={(e) => onChange('state', e.target.value)} placeholder="ST" maxLength={2} className="uppercase" />
        <Input value={holder.zip} onChange={(e) => onChange('zip', e.target.value)} placeholder="ZIP" />
      </div>
      <Input value={holder.email} onChange={(e) => onChange('email', e.target.value)} placeholder="Email (for CC on send)" />
      <Button size="sm" onClick={onCreate}>Save holder</Button>
    </div>
  );
}

function CertToggle({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 py-0.5 text-sm text-text">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-border bg-bg accent-accent" />
      <span>{label}</span>
    </label>
  );
}

function Tristate({ label, value, onChange }) {
  return (
    <div className="flex items-center gap-1 py-0.5">
      <span className="text-xs text-text-muted w-28 truncate" title={label}>{label}</span>
      <select value={value || 'na'} onChange={(e) => onChange(e.target.value)}
        className="rounded border border-border bg-bg px-1.5 py-0.5 text-[11px] text-text">
        <option value="yes">Yes</option>
        <option value="no">No</option>
        <option value="na">N/A</option>
      </select>
    </div>
  );
}

// A coverage line with Yes/No/NA + optional limit + deductible + provisions.
function CoverageLine({ label, line, onChange, noAmounts, noDeductible, showProvisions }) {
  const l = line || { covered: 'na' };
  const covered = l.covered === true ? 'yes' : l.covered === false ? 'no' : (l.covered || 'na');
  const set = (patch) => onChange({ ...l, ...patch });
  return (
    <div className="flex items-center gap-1.5 py-0.5">
      <span className="text-xs text-text-muted flex-1 truncate" title={label}>{label}</span>
      <select value={covered} onChange={(e) => set({ covered: e.target.value })}
        className="rounded border border-border bg-bg px-1 py-0.5 text-[11px] text-text">
        <option value="yes">Yes</option>
        <option value="no">No</option>
        <option value="na">N/A</option>
      </select>
      {!noAmounts ? (
        <MoneyInput value={l.limit ?? ''} onValueChange={({ floatValue }) => set({ limit: floatValue ?? '' })} placeholder="Limit" className="w-20 h-7 px-1.5 text-[11px]" />
      ) : null}
      {!noAmounts && !noDeductible ? (
        // Free text, not MoneyInput: catastrophe deductibles are often a
        // percentage (e.g. wind/hail "2%"), which a whole-dollar field can't hold.
        <Input value={l.deductible ?? ''} onChange={(e) => set({ deductible: e.target.value })} placeholder="Ded" className="w-16 h-7 px-1.5 text-[11px]" />
      ) : null}
      {showProvisions ? (
        <select value={l.subject_to_provisions || 'na'} onChange={(e) => set({ subject_to_provisions: e.target.value })}
          title="Subject to different provisions"
          className="rounded border border-border bg-bg px-1 py-0.5 text-[10px] text-text">
          <option value="yes">SP:Y</option>
          <option value="no">SP:N</option>
          <option value="na">SP:-</option>
        </select>
      ) : null}
    </div>
  );
}

function FieldSet({ legend, children }) {
  return (
    <fieldset className="rounded-card border border-border bg-surface p-3">
      <legend className="px-1 text-[10px] font-semibold uppercase tracking-wider text-text-muted">{legend}</legend>
      <div className="space-y-2">{children}</div>
    </fieldset>
  );
}

function Field({ label, className, children }) {
  return (
    <label className={cn('block', className)}>
      <span className="text-[10px] font-medium uppercase tracking-wide text-text-muted">{label}</span>
      <div className="mt-0.5">{children}</div>
    </label>
  );
}

function PreviewPane({ url, loading, error, formLabel }) {
  return (
    <div className="flex flex-1 min-h-[70vh] flex-col overflow-hidden rounded-card border border-border bg-surface">
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-muted">{formLabel} preview</div>
        {loading ? <div className="text-[11px] text-text-dim">Rendering...</div> : null}
      </div>
      {error ? <div className="border-b border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">{error}</div> : null}
      {url ? (
        <iframe src={url} title="COI preview" className="flex-1 min-h-0" style={{ border: 'none', background: 'white' }} />
      ) : (
        <div className="flex flex-1 items-center justify-center text-sm text-text-muted">
          {loading ? 'Rendering preview...' : 'Preview will appear here.'}
        </div>
      )}
    </div>
  );
}

function SuccessBody({ done, formLabel, holderName }) {
  return (
    <div className="rounded-card border border-success bg-success-soft p-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-success">
        <CheckCircle2 size={16} strokeWidth={1.75} />
        <span>{formLabel} issued to {holderName}</span>
      </div>
      {done.pdf_url ? (
        <a href={done.pdf_url} target="_blank" rel="noopener" className="mt-2 inline-flex items-center gap-1 text-xs text-info hover:underline">
          View PDF <ExternalLink size={11} />
        </a>
      ) : null}
      <p className="mt-2 text-xs text-text-muted">Enter email addresses below to send the certificate.</p>
    </div>
  );
}
